import { createAsyncThunk } from '@reduxjs/toolkit';
import { partyService, type CreatePartyRequest, type UpdatePartyRequest, type CreateAddressRequest, type UpdateAddressRequest, type CreatePartyIdentifierRequest, type UpdatePartyIdentifierRequest } from '../../services/api/partyService';

// --- Party Thunks ---

export const fetchParties = createAsyncThunk(
  'party/fetchParties',
  async ({ tenantId, searchText, status, pageNumber, pageSize }: {
    tenantId: number; searchText?: string; status?: string; pageNumber?: number; pageSize?: number;
  }, { rejectWithValue }) => {
    try {
      return await partyService.getParties(tenantId, searchText, status, pageNumber, pageSize);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch parties');
    }
  }
);

export const fetchPartyById = createAsyncThunk(
  'party/fetchPartyById',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      return await partyService.getPartyById(tenantId, id);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch party');
    }
  }
);

export const createParty = createAsyncThunk(
  'party/createParty',
  async (request: CreatePartyRequest, { rejectWithValue }) => {
    try {
      return await partyService.createParty(request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create party');
    }
  }
);

export const updateParty = createAsyncThunk(
  'party/updateParty',
  async ({ id, request }: { id: number; request: UpdatePartyRequest }, { rejectWithValue }) => {
    try {
      return await partyService.updateParty(id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update party');
    }
  }
);

export const deleteParty = createAsyncThunk(
  'party/deleteParty',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deleteParty(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete party');
    }
  }
);

export const activateParty = createAsyncThunk(
  'party/activateParty',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.activateParty(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to activate party');
    }
  }
);

export const deactivateParty = createAsyncThunk(
  'party/deactivateParty',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deactivateParty(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to deactivate party');
    }
  }
);

// --- Role Thunks ---

export const assignRole = createAsyncThunk(
  'party/assignRole',
  async ({ partyId, tenantId, roleCode }: { partyId: number; tenantId: number; roleCode: string }, { rejectWithValue }) => {
    try {
      return await partyService.assignRole(partyId, { tenantId, roleCode });
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to assign role');
    }
  }
);

export const removeRole = createAsyncThunk(
  'party/removeRole',
  async ({ tenantId, partyId, roleId }: { tenantId: number; partyId: number; roleId: number }, { rejectWithValue }) => {
    try {
      return await partyService.removeRole(tenantId, partyId, roleId);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove role');
    }
  }
);

// --- Address Thunks ---

export const createAddress = createAsyncThunk(
  'party/createAddress',
  async ({ partyId, request }: { partyId: number; request: CreateAddressRequest }, { rejectWithValue }) => {
    try {
      return await partyService.createAddress(partyId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add address');
    }
  }
);

export const updateAddress = createAsyncThunk(
  'party/updateAddress',
  async ({ partyId, addressId, request }: { partyId: number; addressId: number; request: UpdateAddressRequest }, { rejectWithValue }) => {
    try {
      return await partyService.updateAddress(partyId, addressId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update address');
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'party/deleteAddress',
  async ({ tenantId, partyId, addressId }: { tenantId: number; partyId: number; addressId: number }, { rejectWithValue }) => {
    try {
      return await partyService.deleteAddress(tenantId, partyId, addressId);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove address');
    }
  }
);

export const setDefaultAddress = createAsyncThunk(
  'party/setDefaultAddress',
  async ({ tenantId, partyId, addressId }: { tenantId: number; partyId: number; addressId: number }, { rejectWithValue }) => {
    try {
      await partyService.setDefaultAddress(tenantId, partyId, addressId);
      return { partyId, addressId };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to set default address');
    }
  }
);

// --- Identifier Thunks ---

export const fetchPartyIdentifiers = createAsyncThunk(
  'party/fetchPartyIdentifiers',
  async ({ tenantId, partyId }: { tenantId: number; partyId: number }, { rejectWithValue }) => {
    try {
      return await partyService.getPartyIdentifiers(tenantId, partyId);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch party identifiers');
    }
  }
);

export const createPartyIdentifier = createAsyncThunk(
  'party/createPartyIdentifier',
  async ({ partyId, request }: { partyId: number; request: CreatePartyIdentifierRequest }, { rejectWithValue }) => {
    try {
      return await partyService.createPartyIdentifier(partyId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to add identifier');
    }
  }
);

export const updatePartyIdentifier = createAsyncThunk(
  'party/updatePartyIdentifier',
  async ({ partyId, identifierId, request }: { partyId: number; identifierId: number; request: UpdatePartyIdentifierRequest }, { rejectWithValue }) => {
    try {
      return await partyService.updatePartyIdentifier(partyId, identifierId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update identifier');
    }
  }
);

export const deletePartyIdentifier = createAsyncThunk(
  'party/deletePartyIdentifier',
  async ({ tenantId, partyId, identifierId }: { tenantId: number; partyId: number; identifierId: number }, { rejectWithValue }) => {
    try {
      return await partyService.deletePartyIdentifier(tenantId, partyId, identifierId);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove identifier');
    }
  }
);

// --- Definition Thunks ---

export const fetchRoleDefinitions = createAsyncThunk(
  'party/fetchRoleDefinitions',
  async ({ tenantId, activeOnly }: { tenantId: number; activeOnly?: boolean }, { rejectWithValue }) => {
    try {
      return await partyService.getRoleDefinitions(tenantId, activeOnly);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch role definitions');
    }
  }
);

export const createRoleDefinition = createAsyncThunk(
  'party/createRoleDefinition',
  async (request: { tenantId: number; roleCode: string; roleName: string; description?: string; sortOrder?: number }, { rejectWithValue }) => {
    try {
      return await partyService.createRoleDefinition(request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create role definition');
    }
  }
);

export const updateRoleDefinition = createAsyncThunk(
  'party/updateRoleDefinition',
  async ({ id, request }: { id: number; request: { tenantId: number; roleName: string; description?: string; sortOrder?: number } }, { rejectWithValue }) => {
    try {
      return await partyService.updateRoleDefinition(id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update role definition');
    }
  }
);

export const deleteRoleDefinition = createAsyncThunk(
  'party/deleteRoleDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deleteRoleDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete role definition');
    }
  }
);

export const activateRoleDefinition = createAsyncThunk(
  'party/activateRoleDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.activateRoleDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to activate role definition');
    }
  }
);

export const deactivateRoleDefinition = createAsyncThunk(
  'party/deactivateRoleDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deactivateRoleDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to deactivate role definition');
    }
  }
);

export const fetchAddressTypeDefinitions = createAsyncThunk(
  'party/fetchAddressTypeDefinitions',
  async ({ tenantId, activeOnly }: { tenantId: number; activeOnly?: boolean }, { rejectWithValue }) => {
    try {
      return await partyService.getAddressTypeDefinitions(tenantId, activeOnly);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch address type definitions');
    }
  }
);

export const createAddressTypeDefinition = createAsyncThunk(
  'party/createAddressTypeDefinition',
  async (request: { tenantId: number; typeCode: string; typeName: string; description?: string; sortOrder?: number }, { rejectWithValue }) => {
    try {
      return await partyService.createAddressTypeDefinition(request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create address type definition');
    }
  }
);

export const updateAddressTypeDefinition = createAsyncThunk(
  'party/updateAddressTypeDefinition',
  async ({ id, request }: { id: number; request: { tenantId: number; typeName: string; description?: string; sortOrder?: number } }, { rejectWithValue }) => {
    try {
      return await partyService.updateAddressTypeDefinition(id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update address type definition');
    }
  }
);

export const deleteAddressTypeDefinition = createAsyncThunk(
  'party/deleteAddressTypeDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deleteAddressTypeDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete address type definition');
    }
  }
);

export const activateAddressTypeDefinition = createAsyncThunk(
  'party/activateAddressTypeDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.activateAddressTypeDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to activate address type definition');
    }
  }
);

export const deactivateAddressTypeDefinition = createAsyncThunk(
  'party/deactivateAddressTypeDefinition',
  async ({ tenantId, id }: { tenantId: number; id: number }, { rejectWithValue }) => {
    try {
      await partyService.deactivateAddressTypeDefinition(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to deactivate address type definition');
    }
  }
);
